import { FC, useState, useMemo, useCallback, useEffect } from 'react';
import {
  ITreeNodeData,
  ITreeProps,
  TreeActions,
  TreeState,
} from '../interfaces';
import TreeNode from './TreeNode';
import cn from 'classnames';

const findByKey = (nodeKey: string) => (key: string) => nodeKey === key;

const Tree: FC<ITreeProps> = function Tree({
  treeData,
  renderNode,
  onNodeClick,
  onExpandNode,
  searchQuery,
  expandedKeys: exKeys,
  expandCondition = 'always',
  encapsulated,
  className,
}) {
  const [state, setState] = useState<TreeState>(() => ({
    expandedKeys: exKeys || [],
    selectedKeys: [],
  }));

  useEffect(() => {
    if (expandCondition === 'when-collapsed' && state.expandedKeys.length > 0) {
      return;
    }
    setState({
      ...state,
      expandedKeys: exKeys || state.expandedKeys,
    });
  }, [exKeys]);

  const toggleExpand = useCallback(
    (node?: ITreeNodeData) => {
      if (!node) return;
      const index = state.expandedKeys.findIndex(findByKey(node.key));
      let keys: string[] = [];
      if (index === -1) {
        keys = [...state.expandedKeys, node.key];
        setState(() => ({
          ...state,
          expandedKeys: keys,
        }));
        onExpandNode && onExpandNode(keys);
      } else {
        keys = state.expandedKeys.filter((k) => k !== node.key);
        setState(() => ({
          ...state,
          expandedKeys: keys,
        }));
        onExpandNode && onExpandNode(keys);
      }
    },
    [state],
  );

  const select = useCallback(
    (node?: ITreeNodeData) => {
      if (!node?.options?.selectable) return;

      const index = state.selectedKeys.findIndex(findByKey(node.key));

      if (index === -1) {
        setState({
          ...state,
          selectedKeys: [...state.selectedKeys, node.key],
        });
      }
    },
    [state],
  );

  const selectSingle = useCallback(
    (node?: ITreeNodeData) => {
      if (!node?.options?.selectable) return;
      const index = state.selectedKeys.findIndex(findByKey(node.key));

      if (index === -1) setState({ ...state, selectedKeys: [node.key] });
    },
    [state],
  );

  const deselect = useCallback(
    (node?: ITreeNodeData) => {
      if (!node?.options?.selectable) {
        setState({ ...state, selectedKeys: [] });
        return;
      }
      const index = state.selectedKeys.findIndex(findByKey(node.key));

      if (index !== -1) {
        setState({
          ...state,
          selectedKeys: state.selectedKeys.filter((key) => key !== node.key),
        });
      }
    },
    [state],
  );

  const actions: TreeActions = useMemo(
    () => ({
      expand: toggleExpand,
      select,
      selectSingle,
      deselect,
    }),
    [deselect, toggleExpand, select, selectSingle],
  );

  return (
    <div
      className={cn('fd-tree', className, {
        'pl-2 mb-1 rounded border bg-grey-900 p-1': encapsulated,
      })}
    >
      {treeData.map((node: ITreeNodeData) => {
        return (
          <TreeNode
            key={node.key}
            node={node}
            searchQuery={searchQuery || ''}
            depth={0}
            expandedKeys={state.expandedKeys}
            selectedKeys={state.selectedKeys}
            actions={actions}
            renderNode={renderNode}
            onNodeClick={onNodeClick}
          />
        );
      })}
    </div>
  );
};

export default Tree;
